// External libraries
import { useEffect, useMemo, useRef, useState } from "react";
import { GeoId } from "../../types/geo.types";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Search as SearchIcon } from "@carbon/icons-react";

// Components
import { GEO_LEVELS, RouteConstants } from "../../constants";
import { Typography } from "../../components/Shadcn/Typography";
import { Input } from "../../components/Shadcn/Input";
import { Datatable, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/Shadcn/Datatable/Datatable";

// Utilities
import { cifTypeState, compareListState, dropdownList, geoState, selectedGeoDropdownState, selectedGeoNameState, selPOICategories, userCurrencyState } from "../../states";
import { createSearchParams, getCurrentGeo, getSelectedGeoName, setDocumentTitle, useMapHelpers } from "../../helpers";
import { useCompareService, useMapsService } from "../../services";
import { AddToCompare } from "../home";

const GEO_PREREQUISITE: Record<string, string | null> = {
    state: null,
    district: 'state',
    sub_district: 'district',
    village: 'sub_district',
};

export const GeoSearchNew = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const mapServices = useMapsService();
    const compareService = useCompareService();
    const { getCurrencyWithSymbol } = useMapHelpers();
    const selCurrency = useRecoilValue(userCurrencyState);
    const selCategoryList = useRecoilValue(selPOICategories);
    const cifType = useRecoilValue(cifTypeState);
    const compareList = useRecoilValue(compareListState);
    const [searchParams, setSearchParams] = useSearchParams();
    const [geoDropdownList, setGeoDropdownList] = useRecoilState(dropdownList);
    const [selectedGeo, setselectedGeo] = useRecoilState(geoState);
    const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedGeoName, setSelectedGeoName] = useRecoilState(selectedGeoNameState);
    const setSelectedGeoDropdown = useSetRecoilState(selectedGeoDropdownState);
    const [selectedItem, setSelectedItem] = useState<'Sub-District' | 'Pincode'>('Sub-District');
    const inputRef = useRef<HTMLInputElement>(null);

    const handleDropdownToggle = (index: number) => {
        if (index !== 0) {
            setActiveDropdown((prev) => (index));
        }
    };

    const handleNavigateBack = (list: any, geoId: any, curr_level: string, next_geo_level: any, index: number, event: any) => {
        if (!(event.target as HTMLElement).parentElement?.id.includes('geo-navigate-btn')) return;
        if (!list || !geoId) return;

        if (geoId === geoCode) return;

        const foundItem = list.find((item: any) => String(item.geoId) === String(geoId));
        handleGeoSelect(foundItem.geoId, curr_level, next_geo_level, index, foundItem.geoName, foundItem.count);
    }

    const handleInputChange = (value: string) => {
        setSearchTerm(value.toLowerCase());
    };

    const handleGeoSelect = (geo_id: number, curr_level: string, next_geo_level: any, index: number, geoName: string, poiCount?: string) => {
        setSelectedGeoName(geoName);
        const resetFields: any = {
            country: { state: '', district: '', sub_district: '', village: '' },
            state: { district: '', sub_district: '', village: '' },
            district: { sub_district: '', village: '' },
            sub_district: { village: '' },
        };
        handleDropdownToggle(index);
        setSearchTerm('');
        if (curr_level == "pincode") {
            curr_level = "sub_district"
        }

        const updatedGeo = {
            ...selectedGeo,
            [curr_level]: geo_id,
            ...(resetFields[curr_level] || {}),
        }
        setselectedGeo(updatedGeo);
        setTimeout(() => {
            const newSearchParams = createSearchParams(updatedGeo);
            if (location.pathname === RouteConstants.no_poi) {
                navigate(RouteConstants.dashboard + "?" + newSearchParams);
            }
        }, 0);

    }

    const handleSearchParams = (updatedParams: Record<string, string | null>) => {
        const currentParams = new URLSearchParams(searchParams);
        Object.entries(updatedParams).forEach(([key, value]) => {
            if (value) {
                currentParams.set(key, value);
            } else {
                currentParams.delete(key);
            }
        });
        setSearchParams(currentParams);
    };

    const getFilteredData = (data: any[]) => {
        if (!searchTerm) return data;
        return data.filter((item) =>
            item.geoName.toLowerCase().includes(searchTerm)
        );
    };

    const getPayload = (geoId: GeoId) => {
        return {
            geoId,
            type: cifType,
            currency: selCurrency,
            ...selCategoryList,
        }
    }

    const geoCode = useMemo(() => {
        const { country, state, district, sub_district, village } = selectedGeo;
        const geo_id = village || sub_district || district || state || country;
        return geo_id === '1' ? 'EW-IN-IND-22-0' : geo_id;
    }, [selectedGeo]);

    const updateGeoDropdownList = () => {
        setGeoDropdownList((prev) => {
            const updatedList = { ...prev };

            if (geoCode === (selectedGeo.country)) {
                updatedList.districts = null;
                updatedList.sub_districts = null;
                updatedList.villages = null;
            } else if (geoCode === (selectedGeo.state)) {
                updatedList.sub_districts = null;
                updatedList.villages = null;
            } else if (geoCode === (selectedGeo.district)) {
                updatedList.villages = null;
            }
            return updatedList;
        });
    };

    const updateSelGeoName = () => {
        const { country, state, district, sub_district, village } = selectedGeo;
        let geoList: any = [];

        if (village) {
            geoList = geoDropdownList['villages']?.dropDownData || [];
        } else if (sub_district) {
            geoList = geoDropdownList['sub_districts']?.dropDownData || [];
        } else if (district) {
            geoList = geoDropdownList['districts']?.dropDownData || [];
        } else if (state) {
            geoList = geoDropdownList['states']?.dropDownData || [];
        } else if (country) {
            geoList = geoDropdownList['countries']?.dropDownData || [];
        }

        const found = geoList.find((item: any) => String(item.geoId) === String(geoCode));
        if (found) {
            setSelectedGeoName(found.geoName);
        }
    };

    const isEqual = (obj1: any, obj2: any) => {
        const normalize = (value: any) => (!value ? null : String(value));

        const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

        for (const key of keys) {
            if (normalize(obj1[key]) !== normalize(obj2[key])) {
                return false;
            }
        }
        return true;
    };

    const handleAddtoCompare = async (e: any, item: any) => {
        e.stopPropagation();
        if (!item.addToCompare) {
            const payload = {
                addToCompare: [{
                    geoId: item.geoId, level: item.geoLevelNumber,
                    ...(item.geotype === "pincode" && { parent_id: (selectedGeo.district) })
                }]
            }
            await compareService.postCompreList(payload);
        } else {
            navigate(RouteConstants.compare);
        }
    }

    useEffect(() => {
        const currSearchParam = {
            country: searchParams.get("country") || "1",
            state: searchParams.get("state") || "",
            district: searchParams.get("district") || "",
            sub_district: searchParams.get("sub_district") || "",
            village: searchParams.get("village") || "",
        };

        const updatedParams: Record<string, string | null> = {
            country: selectedGeo.country || null,
            state: selectedGeo.state || null,
            district: selectedGeo.district || null,
            sub_district: selectedGeo.sub_district || null,
            village: selectedGeo.village || null,
        };

        if (!isEqual(updatedParams, currSearchParam)) {
            setselectedGeo((prev: any) => ({ ...currSearchParam }));
        }
    }, [searchParams]);


    useEffect(() => {
        if (selectedGeo) {
            const updatedParams: Record<string, string | null> = {
                country: selectedGeo.country || null,
                state: selectedGeo.state || null,
                district: selectedGeo.district || null,
                sub_district: selectedGeo.sub_district || null,
                village: selectedGeo.village || null,
            };

            const currSearchParam = {
                country: searchParams.get("country") || "1",
                state: searchParams.get("state") || null,
                district: searchParams.get("district") || null,
                sub_district: searchParams.get("sub_district") || null,
                village: searchParams.get("village") || null,
            };

            if (!isEqual(updatedParams, currSearchParam)) {
                handleSearchParams(updatedParams);
                updateSelGeoName();
            }
        }
    }, [selectedGeo]);

    // Memoized payloads for each geo level
    const statesPayload = useMemo(() => {
        if (selectedGeo?.country && selCategoryList?.category?.length > 0 && selCategoryList?.subcategory?.length > 0) {
            const countryGeoId = selectedGeo.country === '1' ? 'EW-IN-IND-22-0' : selectedGeo.country;
            return getPayload(countryGeoId);
        }
        return null;
    }, [selectedGeo?.country, selCategoryList, cifType, selCurrency]);

    const districtsPayload = useMemo(() => {
        if (selectedGeo?.state && selCategoryList?.category?.length > 0 && selCategoryList?.subcategory?.length > 0) {
            return getPayload((selectedGeo.state));
        }
        return null;
    }, [selectedGeo?.state, selCategoryList, cifType, selCurrency]);

    const subDistrictsPayload = useMemo(() => {
        if (selectedGeo?.district && selCategoryList?.category?.length > 0 && selCategoryList?.subcategory?.length > 0) {
            return getPayload((selectedGeo.district));
        }
        return null;
    }, [selectedGeo?.district, selCategoryList, cifType, selCurrency]);

    const villagesPayload = useMemo(() => {
        if (selectedGeo?.sub_district && selCategoryList?.category?.length > 0 && selCategoryList?.subcategory?.length > 0) {
            return getPayload((selectedGeo.sub_district));
        }
        return null;
    }, [selectedGeo?.sub_district, selCategoryList, cifType, selCurrency]);

    // TanStack Query hooks for each geo level
    const { data: statesData } = useQuery({
        queryKey: ['geoDropdown', 'states', statesPayload],
        queryFn: async () => {
            if (!statesPayload) return null;
            return await mapServices.getGeoDropdown(statesPayload);
        },
        enabled: !!statesPayload,
        staleTime: 5 * 60 * 1000,
    });

    const { data: districtsData } = useQuery({
        queryKey: ['geoDropdown', 'districts', districtsPayload],
        queryFn: async () => {
            if (!districtsPayload) return null;
            return await mapServices.getGeoDropdown(districtsPayload);
        },
        enabled: !!districtsPayload,
        staleTime: 5 * 60 * 1000,
    });

    const { data: subDistrictsData } = useQuery({
        queryKey: ['geoDropdown', 'sub_districts', subDistrictsPayload],
        queryFn: async () => {
            if (!subDistrictsPayload) return null;
            return await mapServices.getGeoDropdown(subDistrictsPayload);
        },
        enabled: !!subDistrictsPayload,
        staleTime: 5 * 60 * 1000,
    });

    const { data: villagesData } = useQuery({
        queryKey: ['geoDropdown', 'villages', villagesPayload],
        queryFn: async () => {
            if (!villagesPayload) return null;
            return await mapServices.getGeoDropdownForVillage(villagesPayload);
        },
        enabled: !!villagesPayload,
        staleTime: 5 * 60 * 1000,
    });

    // Update dropdown list when query data changes
    useEffect(() => {
        if (statesData) {
            console.log("HHH")
            setGeoDropdownList(prev => ({ ...prev, states: statesData }));
        }
    }, [statesData]);

    useEffect(() => {
        if (districtsData) {
            setGeoDropdownList(prev => ({ ...prev, districts: districtsData }));
        }
    }, [districtsData]);

    useEffect(() => {
        if (subDistrictsData) {
            setGeoDropdownList(prev => ({ ...prev, sub_districts: subDistrictsData }));
        }
    }, [subDistrictsData]);

    useEffect(() => {
        if (villagesData) {
            setGeoDropdownList(prev => ({ ...prev, villages: villagesData }));
        }
    }, [villagesData]);

    useEffect(() => {
        updateGeoDropdownList();
    }, [geoCode]);

    useEffect(() => {
        setDocumentTitle(selectedGeoName);
    }, [selectedGeoName]);

    useEffect(() => {
        if (inputRef.current && activeDropdown) {
            inputRef.current.focus();
        }
    }, [activeDropdown]);

    useEffect(() => {
        if (geoDropdownList && selectedGeo && geoCode) {
            const catchment = searchParams.get("catchment");
            if (catchment) return;
            const selGeo = getCurrentGeo(geoDropdownList, geoCode, selectedGeo);
            setSelectedGeoName(selGeo?.geoName || 'India');
            setSelectedGeoDropdown(selGeo);
        }
    }, [selectedGeo, geoDropdownList]);

    return (
        <div className="w-full flex flex-wrap justify-start items-center h-full relative">
            {GEO_LEVELS.map((geo_level, index) => {
                // Skip rendering country level
                if (geo_level.key === 'country') {
                    return null;
                }
                if (cifType.toString() === 'CTV' && geo_level.key === 'sub_district' && location.pathname !== RouteConstants.dashboard) {
                    return null; // Skip rendering
                }
                const prereqKey = GEO_PREREQUISITE[geo_level.key];
                const isEnabled = !prereqKey || !!selectedGeo[prereqKey as keyof typeof selectedGeo];
                const geoList = geoDropdownList[geo_level.list as keyof typeof geoDropdownList];
                const hasData = !!(geoList?.dropDownData && geoList?.dropDownData.length > 0);
                // old code before pin code
                // const filteredData = getFilteredData(geoList?.dropDownData || []);

                // const compareGeoIds = [
                //     ...(compareList?.districts || []).map((item: any) => item.geoId),
                //     ...(compareList?.subdistricts || []).map((item: any) => item.geoId)
                // ];

                // const updatedFilterData = filteredData.map((item: any) => ({
                //     ...item,
                //     addToCompare: compareGeoIds.includes(item.geoId)
                // }));
                // new code
                const filteredData = getFilteredData(geoList?.dropDownData || []);

                const compareGeoIds = [
                    ...(compareList?.districts || []).map((item: any) => item.geoId),
                    ...(compareList?.subdistricts || []).map((item: any) => item.geoId),
                    ...(compareList?.pincodes || []).map((item: any) => item.geoId),
                    ...(compareList?.villages || []).map((item: any) => item.geoId),
                ];

                let updatedFilterData = filteredData;
                // 🔍 Filter only for sub_district level
                if (geo_level.key === 'sub_district' || geo_level.key === 'pincode') {
                    updatedFilterData = filteredData.filter((item: any) =>
                        selectedItem === 'Sub-District'
                            ? item.geotype === 'sub_district'
                            : item.geotype === 'pincode'
                    );
                }
                // 💡 Then add `addToCompare` flag
                updatedFilterData = updatedFilterData.map((item: any) => ({
                    ...item,
                    addToCompare: compareGeoIds.includes(item.geoId),
                }));
                return (
                    <div
                        key={geo_level.key}
                        className={`flex flex-col items-center h-full relative ${index === 0 ? 'justify-end w-[8.33%] p-0' : 'justify-between w-1/4'}`}
                        onMouseEnter={() => isEnabled && hasData && handleDropdownToggle(index)}
                        onMouseLeave={() => { setActiveDropdown(null); setSearchTerm('') }}
                        onClick={(event) =>
                            isEnabled && handleNavigateBack(
                                geoList?.dropDownData,
                                selectedGeo[geo_level.key as keyof typeof selectedGeo],
                                geo_level.key,
                                GEO_LEVELS[index + 1],
                                index,
                                event
                            )
                        }
                    >
                        <div className="h-full flex items-center min-w-0 overflow-hidden w-full">
                            <div
                                id={`geo-navigate-btn-${index}`}
                                className={`flex items-center gap-[var(--semantic-2xs)] w-full min-w-0 rounded-[var(--semantic-rounded-sm)] px-[var(--semantic-xs)] transition-colors ${hasData && isEnabled ? 'hover:bg-[var(--sidebar-item-hover)] cursor-pointer' : 'cursor-default'}`}
                                style={{ height: '5vh', ...(hasData && isEnabled && !selectedGeo[geo_level.key as keyof typeof selectedGeo] ? { backgroundColor: 'var(--ds-alpha-neutral-4)' } : {}) }}
                            >
                                <Typography
                                    variant="b2"
                                    as="span"
                                    className={`text-left truncate min-w-0 flex-1 ${hasData && isEnabled
                                            ? !!selectedGeo[geo_level.key as keyof typeof selectedGeo]
                                                ? getSelectedGeoName(geoList?.dropDownData, selectedGeo[geo_level.key as keyof typeof selectedGeo], geo_level.key) === selectedGeoName
                                                    ? 'text-[color:var(--ds-solid-neutral-12)] font-medium'
                                                    : 'text-[color:var(--ds-solid-neutral-12)] font-normal'
                                                : 'text-[color:var(--ds-solid-neutral-10)]'
                                            : 'text-[color:var(--ds-solid-neutral-8)]'
                                        }`}
                                >
                                    {getSelectedGeoName(geoList?.dropDownData, selectedGeo[geo_level.key as keyof typeof selectedGeo], geo_level.key)}
                                </Typography>
                                <ChevronDown
                                    size={12}
                                    className={`flex-shrink-0 ${hasData && isEnabled
                                            ? !!selectedGeo[geo_level.key as keyof typeof selectedGeo]
                                                ? 'text-[color:var(--ds-solid-neutral-12)]'
                                                : 'text-[color:var(--ds-solid-neutral-10)]'
                                            : 'text-[color:var(--ds-solid-neutral-8)]'
                                        }`}
                                />
                            </div>
                        </div>
                        <hr className={`m-0 w-full border-[3px] outline-none ${geoCode === (selectedGeo[geo_level.key as keyof typeof selectedGeo]) ? 'border-[color:var(--black-0)] rounded-t' : 'border-transparent'}`} />
                        {isEnabled && hasData && activeDropdown === index && index !== 0 && (
                            <div
                                className="absolute z-[3000] top-full left-0 py-[var(--semantic-xs)] bg-white border border-[rgba(2,74,174,0.07)] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.15)] rounded-[var(--semantic-rounded-lg)] w-auto min-w-fit whitespace-nowrap overflow-hidden flex flex-col gap-[var(--semantic-xs)] items-start"
                            // onMouseLeave={() => setActiveDropdown(null)}
                            >
                                {/* Search + Tabs section */}
                                <div className="flex flex-col gap-[var(--semantic-2xs)] items-start px-[var(--semantic-xs)] w-full">
                                    <Input
                                        ref={inputRef}
                                        value={searchTerm}
                                        onChange={e => handleInputChange(e.target.value)}
                                        placeholder="Search"
                                        leftIcon={<SearchIcon size={16} />}
                                        className="rounded-none border-x-0 border-t-0 border-b shadow-none focus:ring-0"
                                    />
                                    {/* heading for sub district/pincode tab */}
                                    {geo_level.key === 'sub_district' && (
                                        <div className="flex flex-row justify-start items-start gap-[var(--semantic-xs)] w-full border-b border-[rgba(2,74,174,0.07)]">
                                            {['Sub-District', 'Pincode'].map((item) => {
                                                const isActive = selectedItem === item;
                                                return (
                                                    <div key={item} className="flex flex-col items-start justify-end cursor-pointer" onClick={() => { setSelectedItem(item as 'Sub-District' | 'Pincode'); }}>
                                                        <div className="h-[45px] p-[var(--semantic-xs)] flex items-center gap-[var(--semantic-2xs)]">
                                                            <Typography
                                                                variant="b2"
                                                                className={isActive ? 'text-foreground font-medium' : 'text-muted-foreground'}
                                                            >
                                                                {item}
                                                            </Typography>
                                                        </div>
                                                        <div className={`h-[3px] w-full rounded-tl-[2px] rounded-tr-[2px] ${isActive ? 'bg-[color:var(--black-0)]' : 'bg-transparent'}`} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Content section */}
                                <div className="px-[var(--semantic-xs)] w-full overflow-y-auto no-scrollbar max-h-[54.877vh]">
                                    <Datatable containerClassName="border-0 rounded-none">
                                        <TableHeader className="bg-transparent">
                                            <TableRow className="hover:bg-transparent">
                                                <TableHead className="uppercase bg-transparent hover:bg-transparent font-mono text-xs">
                                                    {geo_level.key === 'sub_district' && selectedItem === 'Pincode'
                                                        ? `Pincodes (${updatedFilterData?.length || 0})`
                                                        : `${geo_level.name} (${updatedFilterData?.length || 0})`}
                                                </TableHead>
                                                {geo_level.key == 'state' && cifType.toString() === 'PoI' &&
                                                    <TableHead className="uppercase bg-transparent hover:bg-transparent font-mono text-xs">
                                                        {`District Coverage(${geoList?.totalDistrictCoverage || 0})`}
                                                    </TableHead>
                                                }
                                                {cifType.toString() === 'PoI' &&
                                                    <TableHead className="uppercase bg-transparent hover:bg-transparent font-mono text-xs">
                                                        {geo_level.key === 'sub_district' && selectedItem === 'Pincode'
                                                            ? `PoI Count (${geoList?.totalPoiCount?.pincode || 0})`
                                                            : geo_level.key === 'sub_district' && selectedItem === 'Sub-District'
                                                                ? `PoI Count (${geoList?.totalPoiCount?.subdistrict || 0})`
                                                                : `PoI Count (${(geoList?.totalPoiCount as any)?.[geo_level?.key] || 0})`}
                                                    </TableHead>
                                                }
                                                {cifType.toString() === 'EH' &&
                                                    <TableHead className="uppercase bg-transparent hover:bg-transparent font-mono text-xs">
                                                        {`EH (${geoList?.totalEhCount || 0})`}
                                                    </TableHead>
                                                }
                                                {cifType.toString() === 'CTV' &&
                                                    <TableHead className="uppercase bg-transparent hover:bg-transparent font-mono text-xs">
                                                        {`CTV (${getCurrencyWithSymbol(geoList?.totalCtvCount, geoList.currency)})`}
                                                    </TableHead>
                                                }
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {updatedFilterData?.map(item => (
                                                <TableRow
                                                    key={item.geoId}
                                                    onClick={() => handleGeoSelect(item.geoId, geo_level.key, GEO_LEVELS[index + 1], index, item.geoName, item.count)}
                                                    className="cursor-pointer district-item"
                                                >
                                                    <TableCell className="min-w-[200px]">
                                                        <Typography variant="h5" className="text-muted-foreground text-left px-[var(--semantic-xs)] hover:text-foreground">
                                                            {item.geoName}
                                                        </Typography>
                                                    </TableCell>

                                                    {geo_level.key === 'state' && cifType.toString() === 'PoI' && (
                                                        <TableCell>
                                                            <Typography variant="h5" className="text-muted-foreground text-left px-[var(--semantic-xs)] hover:text-foreground">
                                                                {item.coverage || '--'}
                                                            </Typography>
                                                        </TableCell>
                                                    )}

                                                    {cifType.toString() === 'PoI' && (
                                                        <TableCell>
                                                            <Typography variant="h5" className="text-muted-foreground text-left px-[var(--semantic-xs)] hover:text-foreground">
                                                                {item.poicount || '--'}
                                                            </Typography>
                                                        </TableCell>
                                                    )}

                                                    {cifType.toString() === 'CTV' && (
                                                        <TableCell>
                                                            <Typography variant="h5" className="text-muted-foreground text-left px-[var(--semantic-xs)] hover:text-foreground">
                                                                {getCurrencyWithSymbol(item.ctvcount, geoList.currency)}
                                                            </Typography>
                                                        </TableCell>
                                                    )}

                                                    {cifType.toString() === 'EH' && (
                                                        <TableCell>
                                                            <Typography variant="h5" className="text-muted-foreground text-left px-[var(--semantic-xs)] hover:text-foreground">
                                                                {item.ehpopcount || '--'}
                                                            </Typography>
                                                        </TableCell>
                                                    )}

                                                    {(item.geotype === "district" || item.geotype === 'sub_district' || item.geotype === 'pincode' || item.geotype === 'village') && (
                                                        <TableCell>
                                                            <AddToCompare
                                                                isAddedToCompare={item.addToCompare}
                                                                geoId={item.geoId}
                                                                level={item.geoLevelNumber}
                                                                parent_id={(selectedGeo.district)}
                                                                count={item.geotype === "district"
                                                                    ? compareList?.districts?.length :
                                                                    item.geotype === "sub_district" ? compareList?.subdistricts?.length :
                                                                        item.geotype === "pincode" ? compareList?.pincodes?.length :
                                                                            compareList?.villages?.length}
                                                                tooltipPosition="left"
                                                            />
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Datatable>

                                    {!updatedFilterData || updatedFilterData.length === 0 &&
                                        <Typography variant="h5" className="text-muted-foreground text-center p-[var(--semantic-2xs)] w-full">
                                            No data found
                                        </Typography>}
                                </div>
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}